import { BILLING_OPERATION_RIGHTS } from "../../billing/runtime/rights.ts";
import { type AthenaRightKey, parseAthenaRightKey } from "../../rights/key.ts";
import { listStorageRightKeys } from "../../storage/runtime/rights.ts";
import type {
  AthenaAuthorizationRightDefinition,
  AthenaAuthorizationRiskLevel,
  AthenaAuthorizationScopeKind,
} from "./types.ts";

export const AUTHORIZATION_CATALOG_VERSION = 2;

export function defineAthenaRight(input: {
  description: string;
  displayName: string;
  domain: string;
  key: string;
  risk?: AthenaAuthorizationRiskLevel;
  scope: AthenaAuthorizationScopeKind;
  assignable?: boolean;
}): AthenaAuthorizationRightDefinition {
  return {
    assignable: input.assignable !== false,
    description: input.description,
    displayName: input.displayName,
    domain: input.domain,
    key: parseAthenaRightKey(input.key),
    riskLevel: input.risk ?? "low",
    scopeKind: input.scope,
  };
}

const ORGANIZATION_RIGHTS: readonly AthenaAuthorizationRightDefinition[] = [
  defineAthenaRight({
    description: "List organization members and invitations",
    displayName: "View members",
    domain: "organization",
    key: "organization.members.read",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "Invite, change role, or remove organization members",
    displayName: "Manage members",
    domain: "organization",
    key: "organization.members.write",
    risk: "elevated",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "Assign the organization owner role",
    displayName: "Assign owners",
    domain: "organization",
    key: "organization.owners.assign",
    risk: "critical",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "Delete the organization",
    displayName: "Delete organization",
    domain: "organization",
    key: "organization.lifecycle.delete",
    risk: "critical",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "Send organization invitations",
    displayName: "Invite members",
    domain: "organization",
    key: "organization.members.invite",
    risk: "elevated",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "View organization role templates and assignments",
    displayName: "View organization roles",
    domain: "authorization",
    key: "authorization.roles.read",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "Create or clone organization roles",
    displayName: "Manage organization roles",
    domain: "authorization",
    key: "authorization.roles.write",
    risk: "elevated",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "Delegate organization rights the actor does not hold",
    displayName: "Delegate organization rights",
    domain: "authorization",
    key: "authorization.roles.delegate",
    risk: "critical",
    scope: "organization",
  }),
  defineAthenaRight({
    description: "View platform role templates",
    displayName: "View platform roles",
    domain: "authorization",
    key: "authorization.platform.read",
    scope: "platform",
  }),
  defineAthenaRight({
    description: "Assign platform roles",
    displayName: "Manage platform roles",
    domain: "authorization",
    key: "authorization.platform.write",
    risk: "critical",
    scope: "platform",
  }),
  defineAthenaRight({
    description: "Delegate platform rights the actor does not hold",
    displayName: "Delegate platform rights",
    domain: "authorization",
    key: "authorization.platform.delegate",
    risk: "critical",
    scope: "platform",
  }),
];

const BILLING_RIGHT_META: Record<
  string,
  Pick<
    AthenaAuthorizationRightDefinition,
    "displayName" | "description" | "scopeKind" | "riskLevel"
  >
> = {
  "billing.admin.conflicts.write": {
    description: "Resolve billing ingestion conflicts",
    displayName: "Resolve billing conflicts",
    riskLevel: "critical",
    scopeKind: "platform",
  },
  "billing.admin.ingestion.read": {
    description: "View billing ingestion health",
    displayName: "View billing ingestion",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.admin.reconciliation.write": {
    description: "Run billing reconciliation",
    displayName: "Run billing reconciliation",
    riskLevel: "critical",
    scopeKind: "platform",
  },
  "billing.admin.webhooks.read": {
    description: "View billing webhook status",
    displayName: "View billing webhooks",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.admin.webhooks.write": {
    description: "Reconcile or verify billing webhooks",
    displayName: "Manage billing webhooks",
    riskLevel: "critical",
    scopeKind: "platform",
  },
  "billing.catalog.read": {
    description: "List Athena-owned products and prices",
    displayName: "View catalog",
    riskLevel: "low",
    scopeKind: "self",
  },
  "billing.customers.read": {
    description: "Read merchant customer records",
    displayName: "View customers",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.customers.write": {
    description: "Create or update merchant customers",
    displayName: "Manage customers",
    riskLevel: "elevated",
    scopeKind: "self",
  },
  "billing.payment-links.read": {
    description: "View payment links",
    displayName: "View payment links",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.payment-links.write": {
    description: "Create or update payment links",
    displayName: "Manage payment links",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.payments.read": {
    description: "List merchant-wide payments",
    displayName: "View all payments",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.payments.write": {
    description: "Create or cancel merchant payments",
    displayName: "Manage payments",
    riskLevel: "critical",
    scopeKind: "platform",
  },
  "billing.refunds.read": {
    description: "View refunds",
    displayName: "View refunds",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.refunds.write": {
    description: "Create refunds",
    displayName: "Manage refunds",
    riskLevel: "critical",
    scopeKind: "platform",
  },
  "billing.sales-invoices.read": {
    description: "View sales invoices",
    displayName: "View invoices",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.self.checkout.write": {
    description: "Start or resume own checkout",
    displayName: "Checkout",
    riskLevel: "elevated",
    scopeKind: "self",
  },
  "billing.self.invoices.read": {
    description: "View own invoices",
    displayName: "View own invoices",
    riskLevel: "low",
    scopeKind: "self",
  },
  "billing.self.payments.read": {
    description: "View payments belonging to the current account",
    displayName: "View own payments",
    riskLevel: "low",
    scopeKind: "self",
  },
  "billing.self.subscription.read": {
    description: "View own subscription",
    displayName: "View own subscription",
    riskLevel: "low",
    scopeKind: "self",
  },
  "billing.self.subscription.write": {
    description: "Enroll or cancel own subscription",
    displayName: "Manage own subscription",
    riskLevel: "elevated",
    scopeKind: "self",
  },
  "billing.subscriptions.read": {
    description: "View merchant subscriptions",
    displayName: "View subscriptions",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.subscriptions.write": {
    description: "Create or cancel merchant subscriptions",
    displayName: "Manage subscriptions",
    riskLevel: "critical",
    scopeKind: "platform",
  },
  "billing.webhooks.read": {
    description: "View webhook endpoints",
    displayName: "View webhooks",
    riskLevel: "elevated",
    scopeKind: "platform",
  },
  "billing.webhooks.write": {
    description: "Create or update webhook endpoints",
    displayName: "Manage webhooks",
    riskLevel: "critical",
    scopeKind: "platform",
  },
};

function uniqueBillingRightKeys(): AthenaRightKey[] {
  const keys = new Set<string>();
  for (const required of Object.values(BILLING_OPERATION_RIGHTS)) {
    for (const key of required) {
      keys.add(key);
    }
  }
  return [...keys].sort().map((key) => parseAthenaRightKey(key));
}

function billingDescriptors(): AthenaAuthorizationRightDefinition[] {
  return uniqueBillingRightKeys().map((key) => {
    const meta = BILLING_RIGHT_META[key];
    return {
      assignable: true,
      description: meta?.description ?? `Billing right ${key}`,
      displayName: meta?.displayName ?? key,
      domain: "billing",
      key,
      riskLevel: meta?.riskLevel ?? "elevated",
      scopeKind: meta?.scopeKind ?? "platform",
    };
  });
}

function storageDescriptors(): AthenaAuthorizationRightDefinition[] {
  return listStorageRightKeys().map((key) =>
    defineAthenaRight({
      description: `Storage ${key}`,
      displayName: key === "storage.get" ? "Read files" : key,
      domain: "storage",
      key,
      scope: "self",
    })
  );
}

let cached: readonly AthenaAuthorizationRightDefinition[] | undefined;

export function listAthenaAuthorizationRights(): readonly AthenaAuthorizationRightDefinition[] {
  cached ??= Object.freeze([
    ...billingDescriptors(),
    ...storageDescriptors(),
    ...ORGANIZATION_RIGHTS,
  ]);
  return cached;
}

export function getAthenaAuthorizationRight(
  key: string
): AthenaAuthorizationRightDefinition | undefined {
  return listAthenaAuthorizationRights().find((entry) => entry.key === key);
}

export function assertBillingOperationsReferenceCatalog(): void {
  const registered = new Set(
    listAthenaAuthorizationRights().map((entry) => entry.key)
  );
  for (const [operation, required] of Object.entries(
    BILLING_OPERATION_RIGHTS
  )) {
    for (const key of required) {
      if (!registered.has(key)) {
        throw new Error(
          `Billing operation ${operation} references unregistered right ${key}`
        );
      }
    }
  }
}

export const ORGANIZATION_MEMBERS_READ = parseAthenaRightKey(
  "organization.members.read"
);
export {
  AUTHORIZATION_PLATFORM_WRITE,
  AUTHORIZATION_ROLES_WRITE,
  ORGANIZATION_LIFECYCLE_DELETE,
  ORGANIZATION_MEMBERS_INVITE,
  ORGANIZATION_MEMBERS_WRITE,
} from "./capability-rights.ts";
export const ORGANIZATION_OWNERS_ASSIGN = parseAthenaRightKey(
  "organization.owners.assign"
);
export const AUTHORIZATION_ROLES_READ = parseAthenaRightKey(
  "authorization.roles.read"
);
export const AUTHORIZATION_ROLES_DELEGATE = parseAthenaRightKey(
  "authorization.roles.delegate"
);
export const AUTHORIZATION_PLATFORM_READ = parseAthenaRightKey(
  "authorization.platform.read"
);
export const AUTHORIZATION_PLATFORM_DELEGATE = parseAthenaRightKey(
  "authorization.platform.delegate"
);
export const BILLING_PAYMENTS_READ = parseAthenaRightKey(
  "billing.payments.read"
);
